<template>
  <div>
    <v-overlay :value="isProcessing">
      <v-progress-circular
        indeterminate
        color="primary"
        :size="70"
        :width="7"
      ></v-progress-circular>
    </v-overlay>
    <UniqueUsernameDialog
      :showDialog="showNonUniqueUsername"
      :username="username"
      :onCheck="checkUsername"
      :onClose="() => showNonUniqueUsername = false"
    />
    <v-row justify="center">
      <v-col lg="6" md="5" class="pb-0 hidden-sm-and-down">
        <v-img class="sticky-image" height="100vh" :src="createAccountImage" />
      </v-col>
      <v-col class="register" lg="6" md="7" sm="12">
        <v-alert
          width="100%"
          v-model="isError"
          dismissible
          type="error"
        >{{ errorMessage }}</v-alert>
        <h1 class="register-title">Create your ASCA web account.</h1>
        <v-alert v-if="!!membershipPersonId" type="info" outlined>
          Your membership will be automatically linked to the new account.
        </v-alert>
        <v-form v-model="isValid" class="register-form">
          <v-text-field
            outlined
            required
            label="Create Username"
            type="text"
            v-model="username"
            :rules="[(v) => !!v || 'Username is required']"
          ></v-text-field>
          <v-text-field
            outlined
            required
            label="Create Password"
            type="password"
            v-model="password1"
            :rules="passwordRules"
          ></v-text-field>
          <PasswordRules :password="password1" />
          <v-text-field
            outlined
            required
            label="Confirm Password"
            type="password"
            v-model="password2"
            :rules="[
              (v) => !!v || 'Confirm password is required',
              (v) => v === password1 || 'Passwords do not match'
            ]"
          ></v-text-field>
          <InternationalPhoneNumberInput
            required
            @changeData="(v) => phone = v.number.e164"
          />
          <div>
            <v-checkbox
              outlined
              required
              class="signup-under-18-checkbox"
              label="This is a Junior Account"
              type="check"
              v-model="isJunior"
              :disabled="!!membershipPersonId"
            ></v-checkbox>
          </div>
          <DatepickerFormatted
            v-if="isJunior"
            :date="birthDate"
            :disabled="!!membershipPersonId"
            label="Birth Date"
            v-on:updateDates="updateDates($event, 'birthDate')"
            :rules="[validateIsJunior]"
          />          
        </v-form>
        <v-btn
          :dark="canSubmit()"
          large
          color="primary"
          v-on:click="submit()"
          :disabled="!canSubmit()"
        >Submit</v-btn>
        <p class="pt-3" >Already enrolled? <a href="/login">Login</a></p>

        <div class="footer-container">
          <v-img height="2rem" contain src="../../assets/logo.svg" />
          <footer-text />
        </div>
      </v-col>
    </v-row>
  </div>
</template>

<script lang="ts">
import Vue from 'vue';
import decodeToken from "@/helpers/decode-token";

import DatepickerFormatted from '@/components/common/inputs/DatepickerFormatted.vue';
import UniqueUsernameDialog from '@/components/common/UniqueUsernameDialog.vue';
import FooterText from '@/components/common/FooterText.vue';
import PasswordRules from '@/components/common/PasswordRules.vue';
import InternationalPhoneNumberInput from '@/components/common/inputs/InternationalPhoneNumberInput.vue';

import passwordValidators from '@/helpers/validate-password';
import { isJunior, isUnder16 } from '@/helpers/validate-junior-age';

import authenticationService from "../../services/authenticationService";
import { getErrorMessage } from '@/services/errorHandler';

const createAccountImage = require('@/assets/create-account.png');

export default Vue.extend({
  components: { FooterText, DatepickerFormatted, UniqueUsernameDialog, PasswordRules, InternationalPhoneNumberInput },
  name: 'SignupMember',
  data() {
    return {
      createAccountImage,

      isValid: false,
      isError: false,
      isProcessing: false,
      errorMessage: '',

      showNonUniqueUsername: false,

      showResults: [] as any[],

      dialog: false,

      username: '',
      password1: '',
      password2: '',
      phone: '',
      isJunior: false,
      birthDate: null as Temporal.PlainDate | null,
      passwordRules: passwordValidators,

      membershipPersonId: null
    };
  },
  created: function(){
    this.$data.membershipPersonId = localStorage.getItem("membershipLinkPersonId");
    const isJunior = localStorage.getItem("membershipLinkIsJunior");
    const birthDate = localStorage.getItem("membershipLinkJuniorBirthdate");
    if(isJunior){
      this.$data.isJunior = isJunior;
      this.$data.birthDate = birthDate ? Temporal.PlainDate.from(birthDate) : null;
    }
  },
  methods: {
    processSignup: async function (res: any) {
      const decoded: any = decodeToken(res.token);
      const event = new CustomEvent("userLoaded", {
          detail: { personId: decoded.nameid, token: res.token },
      });
      localStorage.removeItem("membershipLinkPersonId");
      localStorage.removeItem("membershipLinkIsJunior");
      localStorage.removeItem("membershipLinkJuniorBirthdate");
      localStorage.setItem("userToken", res.token);
      window.dispatchEvent(event);
    },
    checkUsername: async function(newUsername?: string) {
      if (!!newUsername) this.username = newUsername;

      try {
        const uniqueUsername = await authenticationService.uniqueUsername(this.username);

        this.showNonUniqueUsername = !uniqueUsername;

        return uniqueUsername;
      } catch (error) {
        this.isError = true;
        this.errorMessage = getErrorMessage(error);
        return false;
      }
    },
    submit: async function() {
      this.isProcessing = true;

      try {
        const uniqueUsername = await this.checkUsername();

        if (uniqueUsername) {
          const response = await authenticationService.createAccountForMember({
          personId: this.membershipPersonId,
          username: this.username,
          password1: this.password1,
          password2: this.password2,
          phone: this.phone,
        });

          if (response.errors != null) {
            this.isError = true;
            this.errorMessage = response.errors ? response.errors.join(',') : 'An Unknown Error Occured';
          } else if (this.isJunior && this.birthDate && isUnder16(this.birthDate)) {

            this.$store.commit('changeParentVerification', { juniorId: response.personId, firstName: '', lastName: '' });
            this.$router.push('/parent-member');
          } else {
            await this.processSignup(response);

            this.$store.commit('changeAgreementFor', 'NewAccount');
            this.$router.push('/agreements');
          }
        }
      } catch (error) {
        this.isError = true;
        this.errorMessage = getErrorMessage(error);
      } finally {
        this.isProcessing = false;
      }
    },
    validateIsJunior(birthDate: Temporal.PlainDate | null) {
      return (
        (!!birthDate && (!!this.membershipPersonId || isJunior(birthDate)))
        || 'Must be under 18 years old'
      )
    },
    canSubmit() {
      return this.isValid && !this.isProcessing && (this.isJunior ? !!this.birthDate : true)
    },
    updateDates: function (val: any, key: string, item: any) {
      this.birthDate = val.date;
      //workaround to force rerendering after setting up formatted date
      this.$set(this, "birthDate", this.birthDate);
    },
  },
});
</script>
